import { cloneDeep } from 'lodash';
import { assignProps, previousCarouselIndex, removeEmpty } from '@/utils/resultsHelper';
import withDraft from '@/utils/draftHelper';
import {
  transformFormObjects,
  safeFormObjects,
  assignFormObjectsEditProps,
  newFormObject,
  findFormSectionByFieldName,
  newFormLink,
  pruneFormFieldErrors,
} from '@/forms/state/state';
import { activeCatalogueFieldArray, catalogueObjectsDialogState, catalogueLinksDialogState } from './state';
import actions from '../constants';
import { datasetSteps } from '../../constants';
import { assignEditProps, markDatasetAsDirty } from '../../state/state';
import findDataset from '../../state/shared';

const updateDatasetCatalogueObjectProperties = withDraft((draft, action) => {
  const item = findDataset(draft, action);

  assignEditProps(item, action.template);
  const field = safeFormObjects(item.catalogue[action.activeFieldName]);
  assignProps(field[action.activeIndex], action.properties);
  item.catalogue[action.activeFieldName] = field;
  markDatasetAsDirty(item, datasetSteps.FIRST_SECTION);
});

const deleteCatalogueObject = withDraft((draft, action) => {
  const item = findDataset(draft, action);

  assignEditProps(item, action.template);
  const items = safeFormObjects(item.catalogue[action.activeFieldName]);
  items.splice(action.activeIndex, 1);
  item.catalogue[action.activeFieldName] = items;
  markDatasetAsDirty(item, datasetSteps.FIRST_SECTION);
});

const showCatalogueLinks = withDraft((draft, action) => {
  const item = findDataset(draft, action);

  const items = cloneDeep(item.catalogue[action.activeFieldName]);
  catalogueLinksDialogState(draft.catalogueFieldItems, safeFormObjects(items), true, {
    ...action,
    permissions: item.permissions,
  });
});

const showCatalogueObjects = withDraft((draft, action) => {
  const item = findDataset(draft, action);

  const items = cloneDeep(item.catalogue[action.activeFieldName]);
  catalogueObjectsDialogState(
    draft.catalogueFieldItems,
    safeFormObjects(items),
    item.isEditing && !item.asViewer.on,
    { ...action, permissions: item.permissions },
  );
});

const applyCatalogueObjects = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  const items = transformFormObjects(removeEmpty(draft.catalogueFieldItems.items));
  const entity = findFormSectionByFieldName(action.template, draft.catalogueFieldItems.activeFieldName);

  assignEditProps(item, action.template);
  item.catalogue[draft.catalogueFieldItems.activeFieldName] = items;
  item.saveErrors = pruneFormFieldErrors(item.saveErrors, {
    fields: [draft.catalogueFieldItems.activeFieldName],
    entity,
  });
  markDatasetAsDirty(item, datasetSteps.FIRST_SECTION);
  draft.catalogueFieldItems = activeCatalogueFieldArray();
});

const addCatalogueLink = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  const field = item.catalogue[action.activeFieldName];
  const items = field ? safeFormObjects(cloneDeep(field)) : [];

  items.push(newFormLink(action.template, action.activeFieldName, draft.catalogueFieldItems.isEditing));
  draft.catalogueFieldItems.isDirty = true;
  catalogueLinksDialogState(draft.catalogueFieldItems, items, true, {
    ...action,
    permissions: item.permissions,
    activeIndex: items.length - 1,
  });
});

const addCatalogueObject = withDraft((draft, action) => {
  const item = findDataset(draft, action);
  const field = item.catalogue[action.activeFieldName];
  const items = field ? safeFormObjects(cloneDeep(field)) : [];

  items.push(newFormObject(action.template, action.activeFieldName, draft.catalogueFieldItems.isEditing));
  draft.catalogueFieldItems.isDirty = true;
  catalogueObjectsDialogState(draft.catalogueFieldItems, items, true, {
    ...action,
    permissions: item.permissions,
    activeIndex: items.length - 1,
  });
});

const hideCatalogueObjects = withDraft((draft) => { draft.catalogueFieldItems = activeCatalogueFieldArray(); });

const updateActiveCatalogueObject = withDraft((draft, { index }) => {
  draft.catalogueFieldItems.activeIndex = index;
});

const updateCatalogueObjectsProperties = withDraft((draft, { properties }) => {
  assignProps(draft.catalogueFieldItems, properties);
});

const updateCatalogueObjectProperties = withDraft((draft, action) => {
  const {
    items, isEditing, activeFieldName, activeIndex,
  } = draft.catalogueFieldItems;

  const item = items[activeIndex];
  assignProps(item, action.properties);
  if (!isEditing) {
    assignFormObjectsEditProps(
      action.template,
      activeFieldName,
      items,
    ).forEach((e) => draft.catalogueFieldItems.saveErrors.push(e));
    draft.catalogueFieldItems.isEditing = true;
  }
  draft.catalogueFieldItems.isDirty = true;
  draft.catalogueFieldItems.saveErrors = pruneFormFieldErrors(draft.catalogueFieldItems.saveErrors, {
    ...action,
    entity: activeIndex.toString(),
  });
});

const editCatalogueObjects = withDraft((draft, action) => {
  const { items, activeFieldName } = draft.catalogueFieldItems;

  assignFormObjectsEditProps(
    action.template,
    activeFieldName,
    items,
  ).forEach((e) => draft.catalogueFieldItems.saveErrors.push(e));
  draft.catalogueFieldItems.isEditing = true;
});

const deleteActiveCatalogueObject = withDraft((draft) => {
  const { items, activeIndex } = draft.catalogueFieldItems;

  items[draft.catalogueFieldItems.activeIndex] = null;
  draft.catalogueFieldItems.activeIndex = previousCarouselIndex(items, activeIndex);
  draft.catalogueFieldItems.isDirty = true;
});

const insertCatalogueLink = withDraft((draft, action) => {
  const {
    items, isEditing, activeFieldName, activeIndex,
  } = draft.catalogueFieldItems;

  const newItem = newFormLink(action.template, activeFieldName, isEditing);
  const itemsState = { ...draft.catalogueFieldItems, ...action, activeIndex: activeIndex + 1 };
  items.splice(activeIndex + 1, 0, newItem);
  catalogueLinksDialogState(draft.catalogueFieldItems, items, !isEditing, itemsState);
  draft.catalogueFieldItems.isDirty = true;
});

const insertCatalogueObject = withDraft((draft, action) => {
  const {
    items, isEditing, activeFieldName, activeIndex,
  } = draft.catalogueFieldItems;

  const newItem = newFormObject(action.template, activeFieldName, isEditing);
  const itemsState = { ...draft.catalogueFieldItems, ...action, activeIndex: activeIndex + 1 };
  items.splice(activeIndex + 1, 0, newItem);
  catalogueObjectsDialogState(draft.catalogueFieldItems, items, !isEditing, itemsState);
  draft.catalogueFieldItems.isDirty = true;
});

export default {
  [actions.ADD_CATALOGUE_LINK]: addCatalogueLink,
  [actions.ADD_CATALOGUE_OBJECT]: addCatalogueObject,
  [actions.APPLY_CATALOGUE_OBJECTS]: applyCatalogueObjects,
  [actions.DELETE_ACTIVE_CATALOGUE_OBJECT]: deleteActiveCatalogueObject,
  [actions.DELETE_CATALOGUE_OBJECT]: deleteCatalogueObject,
  [actions.EDIT_CATALOGUE_OBJECTS]: editCatalogueObjects,
  [actions.HIDE_CATALOGUE_OBJECTS]: hideCatalogueObjects,
  [actions.INSERT_CATALOGUE_LINK]: insertCatalogueLink,
  [actions.INSERT_CATALOGUE_OBJECT]: insertCatalogueObject,
  [actions.SHOW_CATALOGUE_LINKS]: showCatalogueLinks,
  [actions.SHOW_CATALOGUE_OBJECTS]: showCatalogueObjects,
  [actions.UPDATE_ACTIVE_CATALOGUE_OBJECT]: updateActiveCatalogueObject,
  [actions.UPDATE_CATALOGUE_OBJECT_PROPERTIES]: updateCatalogueObjectProperties,
  [actions.UPDATE_CATALOGUE_OBJECTS_PROPERTIES]: updateCatalogueObjectsProperties,
  [actions.UPDATE_DATASET_CATALOGUE_OBJECT_PROPERTIES]: updateDatasetCatalogueObjectProperties,
};
